import { BaseAnalysisEngine, AnalysisResult } from "./baseAnalysisEngine";
import { ReactElement } from "react";
import { ResultDashboard } from "../components/molecules/ResultDashboard";
import {
  ResultDashboardData,
  ResultDiagramIndex,
  ResultMainParam,
  ResultSubParam,
  ResultSupplementaryInfo,
} from "../types";
import { EmotionType } from "../stores/mimosysStore";
import { components } from "../types/voislog_api";

type Mi1AnalysisResponse = AnalysisResult<"mi1">;
type Mi1Emotions = components["schemas"]["Mi1Emotions"];
type RecordingResponse = components["schemas"]["RecordingResponseModel"];

const DEFAULT_DASHBOARD_MAIN_PARAM: ResultMainParam = {
  title: "Result.MIMOSYS_vitality",
  border: "solid 3px primary.theme_lv1",
  value: undefined,
};

const SUB_PARAMS_TITLE_LIST = [
  "Result.MIMOSYS_vivacity",
  "Result.MIMOSYS_relaxation",
];

const SUPPLEMENTARY_INFO_TITLE_LIST = [
  "Result.MIMOSYS_files",
  "Result.MIMOSYS_utterances",
];

export function getEmotionAverage(emotions: EmotionType[]): EmotionType {
  const totalJoy = emotions.reduce((sum, emotion) => sum + emotion.joy, 0);
  const totalAng = emotions.reduce((sum, emotion) => sum + emotion.ang, 0);
  const totalSrw = emotions.reduce((sum, emotion) => sum + emotion.srw, 0);
  const totalClm = emotions.reduce((sum, emotion) => sum + emotion.clm, 0);
  const totalExc = emotions.reduce((sum, emotion) => sum + emotion.exc, 0);
  const averageJoy = parseFloat((totalJoy / emotions.length).toFixed(0));
  const averageAng = parseFloat((totalAng / emotions.length).toFixed(0));
  const averageSrw = parseFloat((totalSrw / emotions.length).toFixed(0));
  const averageClm = parseFloat((totalClm / emotions.length).toFixed(0));
  const averageExc = parseFloat((totalExc / emotions.length).toFixed(0));
  return {
    joy: averageJoy,
    ang: averageAng,
    srw: averageSrw,
    clm: averageClm,
    exc: averageExc,
  };
}

export class MimosysEngine extends BaseAnalysisEngine<"mi1"> {
  private static generateEmotionDiagramParams(
    emotions: Mi1Emotions[],
    recording: RecordingResponse | null,
  ): { width: string; value: string | number }[][] {
    const phraseByFilename = new Map(
      recording?.files.map((f) => [f.filename, f.phrase]) ?? [],
    );
    let phraseIndex = -1;
    let utteranceCount = -1;
    let prevFilename: string | null = null;
    return emotions.map((emotion) => {
      if (prevFilename !== emotion.filename) {
        phraseIndex += 1;
        utteranceCount = 1;
      } else {
        utteranceCount += 1;
      }
      const recordId = `${phraseIndex + 1}${
        utteranceCount === 1 ? "" : "-" + utteranceCount
      }`;
      prevFilename = emotion.filename;
      return [
        { width: "15%", value: recordId },
        { width: "20%", value: phraseByFilename.get(emotion.filename) || "" },
        { width: "15%", value: emotion.joy },
        { width: "15%", value: emotion.ang },
        { width: "15%", value: emotion.srw },
        { width: "15%", value: emotion.clm },
        { width: "15%", value: emotion.exc },
      ];
    });
  }

  private static dashboardDiagramIndex(
    t: (key: string) => string,
  ): ResultDiagramIndex[] {
    return [
      { width: "15%", title: "No" },
      { width: "20%", title: t("Result.table_phrase") },
      { width: "15%", title: t("Result.MIMOSYS_emotion_Joy") },
      { width: "15%", title: t("Result.MIMOSYS_emotion_Ang") },
      { width: "15%", title: t("Result.MIMOSYS_emotion_Srw") },
      { width: "15%", title: t("Result.MIMOSYS_emotion_Clm") },
      { width: "15%", title: t("Result.MIMOSYS_emotion_Exc") },
    ];
  }

  private static errorDashboardData(
    t: (key: string) => string,
  ): ResultDashboardData {
    return {
      mainParam: DEFAULT_DASHBOARD_MAIN_PARAM,
      subParams: [
        { title: SUB_PARAMS_TITLE_LIST[0], value: undefined },
        { title: SUB_PARAMS_TITLE_LIST[1], value: undefined },
      ],
      supplementaryInfo: [
        { title: SUPPLEMENTARY_INFO_TITLE_LIST[0], value: undefined },
        { title: SUPPLEMENTARY_INFO_TITLE_LIST[1], value: undefined },
      ],
      diagramIndex: this.dashboardDiagramIndex(t),
      diagramParams: [],
    };
  }

  private static convertToDashboardData(
    result: Mi1AnalysisResponse,
    recording: RecordingResponse | null,
    t: (key: string) => string,
  ): ResultDashboardData {
    // NOTE: 型定義上はdetailed_analysis_resultはnullableだが、/v2/core/analysis/mi1/recordings/{recording_id}をGETするため必ず存在する
    const { analysis_result, detailed_analysis_result } = result;

    const mainParam: ResultMainParam = {
      ...DEFAULT_DASHBOARD_MAIN_PARAM,
      value: Math.floor(analysis_result.vitality * 100),
    };

    const subParams: ResultSubParam[] = [
      {
        title: SUB_PARAMS_TITLE_LIST[0],
        value: Math.floor(detailed_analysis_result!.vivacity * 100),
      },
      {
        title: SUB_PARAMS_TITLE_LIST[1],
        value: Math.floor(detailed_analysis_result!.relaxation * 100),
      },
    ];

    const supplementaryInfo: ResultSupplementaryInfo[] = [
      {
        title: SUPPLEMENTARY_INFO_TITLE_LIST[0],
        value: recording?.files.length,
      },
      {
        title: SUPPLEMENTARY_INFO_TITLE_LIST[1],
        value: analysis_result.utterances,
      },
    ];

    const diagramParams = MimosysEngine.generateEmotionDiagramParams(
      detailed_analysis_result!.emotions,
      recording,
    );

    return {
      mainParam,
      subParams,
      supplementaryInfo,
      diagramIndex: MimosysEngine.dashboardDiagramIndex(t),
      diagramParams,
    };
  }

  async analyze_call(
    recordingId: string,
    detectVolume: number,
  ): Promise<AnalysisResult<"mi1">> {
    return this._engine.analyze(recordingId, {
      option: { detect_volume: Math.round(detectVolume) },
    });
  }

  async renderResult(
    key: number,
    fileId: string,
    t: (key: string) => string,
  ): Promise<ReactElement> {
    const [result, recording] = await Promise.all([
      this._engine.getResult(fileId),
      this._engine.getRecording(fileId),
    ]);
    const dashboardData = result
      ? MimosysEngine.convertToDashboardData(result, recording, t)
      : MimosysEngine.errorDashboardData(t);
    return (
      <ResultDashboard
        key={key}
        title={t("Result.MIMOSYS_analysis_result")}
        result={dashboardData}
      />
    );
  }

  async getLatestRecordedAt(fileId: string): Promise<string> {
    const result = await this._engine.getResult(fileId);
    return result ? result.recorded_at : "";
  }
}
